import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  UserState,
  SetUserPayload,
  FavoriteMenuItem,
  FavoriteModel,
  FavoriteProject,
  ShareGroup,
  UserConfig,
} from '../../types/store/user.types';

export const initialState: UserState = {
  loginId: '',
  name: '',
  email: '',
  userId: '',
  authority: [],
  avatar: '',
  loginType: 'id',
  userConfigCn: {},
  favoriteList: [
    { shortLabel: 'N', label: '신규' },
    { shortLabel: 'O', label: '열기' },
    { shortLabel: 'S', label: '저장' },
    { shortLabel: 'E', label: '엔터티 추가' },
    { shortLabel: 'IR', label: '식별 관계선 선택' },
    { shortLabel: 'NR', label: '비식별 관계선 선택' },
  ],
  favMdlrmdlCn: [],
  favProjectCn: [],
  shareGroups: [],
  shareGroupsLoading: false,
  shareGroupsError: null,
};

export const userSlice = createSlice({
  name: 'auth/user',
  initialState,
  reducers: {
    setUser: (_state, action: PayloadAction<SetUserPayload>) => {
      return {
        ...initialState,
        ...action.payload,
        loginType: action.payload.loginType || initialState.loginType,
        favoriteList: action.payload.favoriteList || initialState.favoriteList,
        favMdlrmdlCn: action.payload.favMdlrmdlCn || initialState.favMdlrmdlCn,
        favProjectCn: action.payload.favProjectCn || initialState.favProjectCn,
        userConfigCn: action.payload.userConfigCn || initialState.userConfigCn,
        shareGroups: action.payload.shareGroups || initialState.shareGroups,
        shareGroupsLoading: initialState.shareGroupsLoading,
        shareGroupsError: initialState.shareGroupsError,
      };
    },

    userLoggedOut: () => initialState,

    setUserConfigCn: (state, action: PayloadAction<UserConfig>) => {
      state.userConfigCn = {
        ...state.userConfigCn,
        ...action.payload,
      };
    },

    setFavoriteList: (state, action: PayloadAction<FavoriteMenuItem[]>) => {
      state.favoriteList = [...action.payload];
    },

    addFavorite: (state, action: PayloadAction<FavoriteMenuItem>) => {
      if (!state.favoriteList) {
        state.favoriteList = [...initialState.favoriteList];
      }
      if (!state.favoriteList.some(item => item.shortLabel === action.payload.shortLabel)) {
        state.favoriteList.push(action.payload);
      }
    },

    removeFavorite: (state, action: PayloadAction<string>) => {
      if (!state.favoriteList) {
        state.favoriteList = [...initialState.favoriteList];
        return;
      }
      state.favoriteList = state.favoriteList.filter(
        item => item.shortLabel !== action.payload
      );
    },

    setFavMdlrmdlCn: (state, action: PayloadAction<FavoriteModel[]>) => {
      state.favMdlrmdlCn = action.payload;
    },

    addFavMdlrmdl: (state, action: PayloadAction<FavoriteModel>) => {
      if (!state.favMdlrmdlCn) {
        state.favMdlrmdlCn = [];
      }
      const exists = state.favMdlrmdlCn.some(
        item => item && item.mdlrmdlId === action.payload.mdlrmdlId
      );
      if (!exists) {
        state.favMdlrmdlCn.push(action.payload);
      }
    },

    moveFavMdlrmdlToFront: (state, action: PayloadAction<string>) => {
      if (!state.favMdlrmdlCn || state.favMdlrmdlCn.length === 0) {
        return;
      }
      const mdlrmdlId = action.payload;
      const index = state.favMdlrmdlCn.findIndex(
        item => item && item.mdlrmdlId === mdlrmdlId
      );
      if (index > 0) {
        const [item] = state.favMdlrmdlCn.splice(index, 1);
        state.favMdlrmdlCn.unshift(item);
      }
    },

    removeFavMdlrmdl: (state, action: PayloadAction<string>) => {
      if (!state.favMdlrmdlCn) {
        state.favMdlrmdlCn = [];
        return;
      }
      state.favMdlrmdlCn = state.favMdlrmdlCn.filter(
        item => item && item.mdlrmdlId !== action.payload
      );
    },

    setFavProjectCn: (state, action: PayloadAction<FavoriteProject[]>) => {
      state.favProjectCn = action.payload;
    },

    addFavProject: (state, action: PayloadAction<FavoriteProject>) => {
      if (!state.favProjectCn) {
        state.favProjectCn = [];
      }
      const exists = state.favProjectCn.some(
        item => item && item.projectNm === action.payload.projectNm
      );
      if (!exists) {
        state.favProjectCn.push(action.payload);
      }
    },

    moveFavProjectToFront: (state, action: PayloadAction<string>) => {
      if (!state.favProjectCn || state.favProjectCn.length === 0) {
        return;
      }
      const projectNm = action.payload;
      const index = state.favProjectCn.findIndex(
        item => item && item.projectNm === projectNm
      );
      if (index > 0) {
        const [item] = state.favProjectCn.splice(index, 1);
        state.favProjectCn.unshift(item);
      }
    },

    removeFavProject: (state, action: PayloadAction<string>) => {
      if (!state.favProjectCn) {
        state.favProjectCn = [];
        return;
      }
      state.favProjectCn = state.favProjectCn.filter(
        item => item && item.projectNm !== action.payload
      );
    },

    setShareGroups: (state, action: PayloadAction<ShareGroup[]>) => {
      state.shareGroups = action.payload || [];
      state.shareGroupsLoading = false;
      state.shareGroupsError = null;
    },

    setShareGroupsLoading: (state, action: PayloadAction<boolean>) => {
      state.shareGroupsLoading = action.payload;
    },

    setShareGroupsError: (state, action: PayloadAction<string | null>) => {
      state.shareGroupsError = action.payload;
      state.shareGroupsLoading = false;
    },

    clearShareGroups: (state) => {
      state.shareGroups = [];
      state.shareGroupsLoading = false;
      state.shareGroupsError = null;
    },
  },
});

export const {
  setUser,
  userLoggedOut,
  setUserConfigCn,
  setFavoriteList,
  addFavorite,
  removeFavorite,
  setFavMdlrmdlCn,
  addFavMdlrmdl,
  moveFavMdlrmdlToFront,
  removeFavMdlrmdl,
  setFavProjectCn,
  addFavProject,
  moveFavProjectToFront,
  removeFavProject,
  setShareGroups,
  setShareGroupsLoading,
  setShareGroupsError,
  clearShareGroups,
} = userSlice.actions;

export default userSlice.reducer;
